import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconDelete, IconEdit, IconFactory } from 'svgIcons/MotionPortalIcons';
import { InfoMessage, GeneralDialog } from 'sharedComponents';
import { popupTypes } from 'helpers/constants';
import { ALLOWED_ASSET_TYPES, GENERIC_MACHINE_SUBTYPES, MIN_ASSETS_FOR_CONVERSION_BANNER } from '../PowertrainWizard/components/steps/PhysicalConnectionStep/constants';
import { useUserCapability } from 'sharedHooks';

import PowertrainBasicInfo from '../SharedComponents/PowertrainBasicInfo';
import PowertrainAssetsConfiguration from '../SharedComponents/PowertrainAssetsConfiguration';
import { ConvertToLocationBanner, ConvertToLocationModal } from '../ConvertToLocation';
import { getSiteIds, getOrganizationIds } from '../helpers';
import './style.scss';

const PowertrainDetails = ({
    powertrain,
    selectedSites,
    organizations,
    deletePowertrain,
    setSelectedPowertrain,
    setPowertrainWizardVisible,
    setPowertrainForEditing,
    searchboxFilter,
    convertToLocationRequest,
    isNotInHierarchy
}) => {
    const { t: translate } = useTranslation();
    const userCapability = useUserCapability();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [bannerDismissedForPowertrain, setBannerDismissedForPowertrain] = useState(null);
    const detailsRef = useRef(null);

    // Reset banner dismissed state and scroll position when switching to a different powertrain
    useEffect(() => {
        setBannerDismissedForPowertrain(null);
        if (detailsRef.current) {
            detailsRef.current.scrollTop = 0;
        }
    }, [powertrain?.powertrainId]);


    // Safe destructuring with fallback for new structure
    const {
        powertrainName,
        siteName,
        connectedAssets: rawConnectedAssets = [],
        notConnectedAssets: rawNotConnectedAssets = [],
        locationName
    } = powertrain || {};

    // Transform connectedAssets to match expected format for PowertrainAssetsConfiguration
    const transformConnectedAssets = (asset) => ({
        id: String(asset.id || asset.assetId || ''),
        assetId: String(asset.assetId || asset.id || ''),
        assetName: asset.assetName || asset.name,
        type: (asset.type || asset.assetType)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.subType || asset.assetSubType || asset.type || asset.assetType || 'Unknown Type' : asset.type || asset.assetType || asset.assetTypeName || 'Unknown Type',
        subType: (asset.type || asset.assetType)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.subType || asset.assetSubType || asset.type || asset.assetType || 'Unknown Type' : '',
        inputSpeedRatio: asset.inputSpeedRatio,
        outputSpeedRatio: (asset.subType || asset.selectedSubType || asset.assetSubType)?.toLowerCase() === GENERIC_MACHINE_SUBTYPES[0] ? asset.outputSpeedRatio || asset.speedRatio : ''
    });

    // Transform notConnectedAssets to match expected format for PowertrainAssetsConfiguration
    const transformNotConnectedAssets = (asset) => ({
        id: String(asset.id || asset.assetId || ''),
        assetId: String(asset.assetId || asset.id || ''),
        assetName: asset.assetName || asset.name,
        type: (asset.type || asset.assetType)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.subType || asset.assetSubType || asset.type || asset.assetType || 'Unknown Type' : asset.type || asset.assetType || asset.assetTypeName || 'Unknown Type',
    });

    const connectedAssets = rawConnectedAssets?.map(transformConnectedAssets) || [];
    const notConnectedAssets = rawNotConnectedAssets?.map(transformNotConnectedAssets) || [];

    // Check if powertrain is eligible for conversion to location
    // Eligible if: powertrain is under "Powertrains not in hierarchy" group,
    // has no physical connections, and user has hierarchy configuration capability
    const hasNoPhysicalConnections = rawConnectedAssets?.length === 0;
    const hasHierarchyConfiguration = userCapability?.hasHierarchyConfiguration?.() || false; /*TRUE FOR REC, Country Admin, Principal & PT_DEV */
    const isEligibleForConversion = isNotInHierarchy && hasNoPhysicalConnections && hasHierarchyConfiguration;
    // Banner is dismissed only for the current powertrain session
    const isBannerDismissed = bannerDismissedForPowertrain === powertrain?.powertrainId;
    const showConvertBanner = isEligibleForConversion && !isBannerDismissed && notConnectedAssets?.length > MIN_ASSETS_FOR_CONVERSION_BANNER;

    // Handlers for convert to location feature
    const handleConvertClick = () => {
        setShowConvertModal(true);
    };

    const handleKeepAsPowertrain = () => {
        // Dismiss banner only for the current powertrain
        setBannerDismissedForPowertrain(powertrain?.powertrainId);
    };

    const handleBannerClose = () => {
        // Dismiss banner only for the current powertrain
        setBannerDismissedForPowertrain(powertrain?.powertrainId);
    };

    const handleConvertConfirm = () => {
        const organizationId = organizations?.[0]?.organizationID;
        convertToLocationRequest(powertrain?.powertrainId, powertrain?.siteId, organizationId);
        setShowConvertModal(false);
    };

    const handleConvertModalClose = () => {
        setShowConvertModal(false);
    };

    // Early return if powertrain is null
    if (!powertrain) {
        return (
            <div className={'powertrain-details'}>
                <InfoMessage title='ABB.Powertrain.Frontend.noPowertrainSelected' />
            </div>
        );
    }

    const handlePowertrainDelete = (powertrainId) => {
        deletePowertrain(powertrainId, getOrganizationIds(organizations), getSiteIds(selectedSites), searchboxFilter);
        setShowDeleteDialog(false);
        setSelectedPowertrain(null);
    };

    const handlePowertrainEdit = () => {
        setPowertrainForEditing(powertrain);
        setPowertrainWizardVisible(true);
    };

    const renderDeleteDialog = () => {
        return <GeneralDialog
            show={showDeleteDialog}
            close={() => setShowDeleteDialog(false)}
            title={translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_Details_DeleteDialog_title')}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.yesLabel'),
                onClick: () => handlePowertrainDelete(powertrain?.powertrainId)
            }}
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.noLabel'),
                onClick: () => setShowDeleteDialog(false)
            }}
            notificationType={popupTypes.WARNING}
        >
            <>{translate('ABB.Powertrain.Frontend.ptToolsPowertrainConfigurationTab_Details_DeleteDialog_body')}</>
        </GeneralDialog>;
    };

    const renderHeader = () => {
        return <div className={'powertrain-details_header'}>
            <div className={'powertrain-details_header_labels'}>
                <div className={'powertrain-details_header_labels_name'}>
                    {powertrainName}
                </div>
                <div className={'powertrain-details_header_labels_site'}>
                    <IconFactory />
                    {siteName}
                </div>
            </div>
            <div className={'powertrain-details_header_buttons'}>
                <IconEdit onClick={handlePowertrainEdit} />
                <IconDelete
                    onClick={() => setShowDeleteDialog(true)}
                    width={18}
                    height={22}
                />
            </div>
        </div>;
    };

    return <div className={'powertrain-details'} ref={detailsRef}>
        {renderHeader()}

        {/* Convert to Location Banner - shown for eligible powertrains */}
        <div className={'powertrain-details_section'}>
            <ConvertToLocationBanner
                onConvertClick={handleConvertClick}
                onKeepClick={handleKeepAsPowertrain}
                onClose={handleBannerClose}
                isVisible={showConvertBanner}
            />
        </div>

        {/* Basic powertrain information */}
        <div className={'powertrain-details_section'}>
            <PowertrainBasicInfo
                powertrainName={powertrainName}
                siteName={siteName}
                locationName={locationName}
                showPlaceholders={true}
            />
        </div>

        {/* Assets configuration showing connected and not connected assets */}
        <div className={'powertrain-details_section'}>
            <PowertrainAssetsConfiguration
                connectedAssets={connectedAssets}
                notConnectedAssets={notConnectedAssets}
            />
        </div>
        {renderDeleteDialog()}

        {/* Convert to Location Modal */}
        <ConvertToLocationModal
            show={showConvertModal}
            onClose={handleConvertModalClose}
            onConfirm={handleConvertConfirm}
            powertrainName={powertrainName}
        />
    </div>;
};

PowertrainDetails.propTypes = {
    powertrain: PropTypes.shape({
        powertrainId: PropTypes.number,
        powertrainName: PropTypes.string,
        siteId: PropTypes.number,
        siteName: PropTypes.string,
        connectedAssets: PropTypes.array,
        notConnectedAssets: PropTypes.array,
    }),
    selectedSites: PropTypes.array,
    organizations: PropTypes.array,
    searchboxFilter: PropTypes.string,
    deletePowertrain: PropTypes.func,
    setPowertrainWizardVisible: PropTypes.func,
    setPowertrainForEditing: PropTypes.func,
    convertToLocationRequest: PropTypes.func,
    isNotInHierarchy: PropTypes.bool,
};

export default PowertrainDetails;
